/**
 * Teacher dashboard overview (brief §teacher: `/teacher`). Class-wide stat
 * tiles plus the per-chapter accuracy chart — everything from a single
 * `GET /api/teacher/overview` call. The CSV export lives here as the
 * dashboard's one explicit, user-initiated download (spec §4.9).
 */
import { Download } from "lucide-react";
import { Button, Card, EmptyState, PageHeader, Spinner, StatTile } from "@client/components/ui";
import { useToast } from "@client/components/ui";
import { useLang, useT, useTp } from "@client/i18n";
import { formatNumber, formatPercent, formatPoints } from "@client/lib/format";
import { messageForError } from "@client/lib/errors";
import { useState } from "react";
import { ChapterAccuracyChart } from "./ChapterAccuracyChart";
import { OnboardingTotals } from "./OnboardingTotals";
import { TeacherNav } from "./TeacherNav";
import { downloadAttemptsCsv, useTeacherOverview } from "./api";

export function TeacherPage() {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const toast = useToast();
  const query = useTeacherOverview();
  const [exporting, setExporting] = useState(false);
  const [exportedRows, setExportedRows] = useState(0);

  async function handleExport() {
    setExporting(true);
    setExportedRows(0);
    try {
      await downloadAttemptsCsv(setExportedRows);
    } catch (error) {
      toast.error(messageForError(error, t));
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title={t("teacher.nav")}
        description={t("teacher.overview.description")}
        actions={
          <Button
            variant="secondary"
            iconLeft={<Download className="h-4 w-4" />}
            loading={exporting}
            onClick={handleExport}
          >
            {exporting && exportedRows > 0
              ? tp("teacher.export.progress", exportedRows, {
                  count: formatNumber(exportedRows, lang),
                })
              : t("teacher.export.cta")}
          </Button>
        }
      />
      <TeacherNav />

      {query.isLoading && <Spinner size="md" label={t("common.loading")} />}
      {!query.isLoading && !query.data && <EmptyState title={t("common.error")} />}

      {query.data && (
        <>
          <div className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile
              label={t("teacher.overview.stats.students.label")}
              value={formatNumber(query.data.students.total, lang)}
              hint={t("teacher.overview.stats.students.hint", {
                active7d: formatNumber(query.data.students.activeLast7d, lang),
                active30d: formatNumber(query.data.students.activeLast30d, lang),
              })}
            />
            <StatTile
              label={t("teacher.overview.stats.attempts.label")}
              value={formatNumber(query.data.attempts.total, lang)}
              hint={t("teacher.overview.stats.attempts.hint", {
                count: formatNumber(query.data.attempts.last7d, lang),
              })}
            />
            <StatTile
              label={t("teacher.overview.stats.exams.label")}
              value={formatNumber(query.data.exams.total, lang)}
              hint={t("teacher.overview.stats.exams.hint", {
                score: formatPoints(query.data.exams.averageScore, lang),
              })}
            />
            <StatTile
              label={t("teacher.overview.stats.verdicts.label")}
              value={`${query.data.exams.verdicts.VG} VG · ${query.data.exams.verdicts.G} G · ${query.data.exams.verdicts.U} U`}
            />
          </div>

          <section>
            <Card title={t("teacher.overview.chart.title")} compact>
              <ChapterAccuracyChart chapters={query.data.chapters} />
              {/*
               * Both series, never a blended accuracy (spec §4.1) — same rule
               * the chart itself follows (`ChapterAccuracyChart.tsx`). This
               * table used to print only `autoAccuracy`, so a chapter whose
               * auto-graded work happened to be 0% read as "0%" here even
               * while the chart's own "självrättat" bar showed 100% for the
               * self-graded half of the same chapter — silently dropping a
               * whole series a teacher would reasonably read as the truth.
               */}
              <dl className="mt-4 grid grid-cols-2 gap-x-2 gap-y-3 text-xs text-ink-600 dark:text-ink-400 sm:grid-cols-4">
                {query.data.chapters.map((c) => (
                  <div key={c.chapter}>
                    <dt className="mb-0.5 font-medium text-ink-700 dark:text-ink-300">
                      {t("teacher.chapterLabel")} {c.chapter}
                    </dt>
                    <dd className="flex items-center justify-between gap-2 tabular-nums">
                      <span>{t("teacher.overview.chart.auto")}</span>
                      <span>{formatPercent(c.autoAccuracy, lang)}</span>
                    </dd>
                    <dd className="flex items-center justify-between gap-2 tabular-nums">
                      <span>{t("teacher.overview.chart.self")}</span>
                      <span>{formatPercent(c.selfAccuracy, lang)}</span>
                    </dd>
                  </div>
                ))}
              </dl>
            </Card>
          </section>

          {query.data.onboarding && <OnboardingTotals counts={query.data.onboarding} />}
        </>
      )}
    </div>
  );
}

export default TeacherPage;
